<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import type { IdentityProfile } from '@Types/IdentityProfile.interface';
import type { USState, ValidationRules } from '@Shared';
import { LINKS } from "@Constants/links.constants";
import SecuredDataGraphic from '@Assets/images/secured-data.svg';
import { FlowLayout, ActionBarLayout, US_STATES, useFormValidation, useFieldStatus} from '@Shared';
// import useIdentityProfileForm from '@Use/useIdentityProfileForm';
import useIdentityProfileForm, { normalizeZipCode } from '../use/useIdentityProfileForm';
import useFlowNavigation from '@Use/useFlowNavigation';
// import useWhatToExpect from '@Use/useWhatToExpect';
import { verifyIdentityApi, getKycUserProfileApi } from '@Api/identity.api';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import useCompanyImageBaseUrl from '@Src/use/useCompanyImageBaseUrl';
import { PaymentTracking } from '@Analytics/index';
import { refreshUserIdentity } from 'host/mfeBridge';

// Stores / base data
const companyImageBaseUrl = useCompanyImageBaseUrl();
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const { createEmptyIdentityProfile } = useIdentityProfileForm();


// Form state
const identityInfo = ref<IdentityProfile>(createEmptyIdentityProfile());
const selectedState = ref<USState | null>(null);
const isSavng = ref(false);
const identityMoreInfoViewed = ref(false);


// Flow / Navigation
const { advanceFlow } = useFlowNavigation();
// const { getProgressSteps, getCurrentStep } = useWhatToExpect();
// const steps = getProgressSteps();

// Form validation
const validationRules = ref<ValidationRules>({
    'firstName': [{ required: true, message: 'First name is required' }],
    'lastName': [{ required: true, message: 'Last name is required' }],
    'email': [
        { required: true, message: 'Email is required' },
        { validateEmail: true, message: 'Valid email is required' }
    ],
    'dateOfBirth': [
        { required: true, message: 'Date of birth is required' },
        { validatePastDate: true, message: 'Please provide a valid date' },
    ],
    'ssn': [
        { required: true, message: 'Last 4 digits of SSN is required' },
        { validate4Ssn: true, message: 'Last 4 digits of SSN is required' }
    ],
    'addressLine1': [{ required: true, message: 'Home address is required' }],
    'city': [{ required: true, message: 'City is required' }],
    'state': [{ required: true, message: 'State is required' }],
    'zip': [
        { required: true, message: 'Zip code is required' },
        { validateZipCode: true, message: 'Valid U.S. zip code is required' }
    ],
});

const formValidation = useFormValidation(identityInfo, validationRules);
const {
    turnOnValidation,
    hasError,
} = formValidation;

const {
    getFieldStatus,
    getFieldMessage
} = useFieldStatus(formValidation);


// Actions
const viewIdentityMoreInfo = () => {
    identityMoreInfoViewed.value = true;
};

const verifyIdentity = async () => {
    await turnOnValidation(true);

    if (!hasError.value) {
        isSavng.value = true;

        const identity = identityInfo.value;
        await verifyIdentityApi(identity);
        PaymentTracking.trackFullKycCompleted('Payments', identityMoreInfoViewed.value);

        await refreshUserIdentity();

        await advanceFlow();
        isSavng.value = false;
        
    }
};

const updateState = (state: USState) => {
    identityInfo.value.state = state.abbreviation;
};

const initializeIdentityInfo = async () => {
    const userProfile = await getKycUserProfileApi();
    if (userProfile) {
        if (userProfile.firstName)    identityInfo.value.firstName    = userProfile.firstName;
        if (userProfile.lastName)     identityInfo.value.lastName     = userProfile.lastName;
        if (userProfile.email)        identityInfo.value.email        = userProfile.email;
        if (userProfile.dateOfBirth)  identityInfo.value.dateOfBirth  = userProfile.dateOfBirth;
        if (userProfile.addressLine1) identityInfo.value.addressLine1 = userProfile.addressLine1;
        if (userProfile.addressLine2) identityInfo.value.addressLine2 = userProfile.addressLine2;
        if (userProfile.city)         identityInfo.value.city         = userProfile.city;
        if (userProfile.state)        identityInfo.value.state        = userProfile.state;
        // Normalize the prefilled ZIP so the model matches the masked field (PLAT-2631).
        if (userProfile.zip)          identityInfo.value.zip          = normalizeZipCode(userProfile.zip);

        if (userProfile.state)
            selectedState.value = US_STATES.find((s: USState) => s.abbreviation === userProfile.state) ?? null;
    }
    // SSN is intentionally never pre-filled
}

// Lifecycle hooks
onMounted(async () => {
    await initializeIdentityInfo();
    PaymentTracking.trackFullKycStarted('Payments');
});


</script>


<template>
    <flow-layout
        suppress-back-to-dashboard
        is-white-label
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl">

        <template #header>Verify your identity</template>

        <app-brand-loader v-if="isSavng" class="loader">
            Verifying your information...
            <template #caption>
                This may take a few seconds, do not refresh this page
            </template>
        </app-brand-loader>

        <app-form-content-layout v-if="!isSavng">
            <h1 class="page-title">Verify your identity</h1>
            <app-highlight-banner class="highlight-banner">
                <template #banner-image-left>
                    <img
                        alt="Magnifying Glass"
                        :src="SecuredDataGraphic" />

                </template>
                <p class="highlight-banner__text">
                    This information is legally required and is used to protect your account.
                    <app-link 
                        :link-url="LINKS.CertifID.VerifyIdentityMoreInfo" 
                        @onClick="viewIdentityMoreInfo"
                        data-id="more-info-url"
                        type="external">Read more</app-link>.
                </p>
            </app-highlight-banner>

            <app-text-input 
                auto-focus
                is-required
                label="First name" 
                v-model="identityInfo.firstName" 
                placeholder="Legal first name" 
                :maxlength="50"
                :field-status="getFieldStatus('firstName')"
                :message="getFieldMessage('firstName')"
                data-validation-field="firstName" 
                data-id="identity-first-name" />

            <app-text-input 
                v-model="identityInfo.lastName"
                label="Last name"
                placeholder="Legal last name"
                is-required
                :maxlength="50"
                :field-status="getFieldStatus('lastName')"
                :message="getFieldMessage('lastName')"
                data-validation-field="lastName"
                data-id="identity-last-name" />

            <app-text-input 
                v-model="identityInfo.email"
                label="Email address" 
                placeholder="Personal email"
                is-required 
                :maxlength="240"
                input-mode="email"
                :field-status="getFieldStatus('email')"
                :message="getFieldMessage('email')"
                data-validation-field="email"
                data-id="identity-email" />

            <app-masked-input 
                v-model="identityInfo.dateOfBirth"
                mask-value
                label="Date of birth"
                mask="##/##/####" 
                placeholder="mm/dd/yyyy"
                :maxlength="8"
                input-mode="numeric"
                is-required 
                :field-status="getFieldStatus('dateOfBirth')"
                :message="getFieldMessage('dateOfBirth')"
                data-validation-field="dateOfBirth"
                data-id="identity-dob" />

            <app-masked-input 
                v-model="identityInfo.ssn"
                label="Last 4 digits of SSN"
                mask="####"
                placeholder="****"
                :maxlength="4" 
                is-required
                input-mode="numeric"
                :field-status="getFieldStatus('ssn')"
                :message="getFieldMessage('ssn')"
                data-validation-field="ssn"
                data-id="identity-first-4ssn">
                <template #focus-message>
                    We're required to collect this to meet regulatory requirements.
                </template>
            </app-masked-input>

            <app-divider />

            <div class="form-subheader">
                Current home address
            </div>

            <app-text-input 
                v-model="identityInfo.addressLine1"
                label="Address line"
                placeholder="Street address"
                :maxlength="80" 
                is-required
                :field-status="getFieldStatus('addressLine1')"
                :message="getFieldMessage('addressLine1')"
                data-validation-field="addressLine1"
                data-id="identity-address-1" >
                <template #focus-message>
                    Enter the home address that you currently reside in. 
                </template>
            </app-text-input>

            <app-text-input 
                v-model="identityInfo.addressLine2"
                label="Apartment, suite, etc."
                :maxlength="50" 
                data-id="identity-address-2" />

            <app-text-input 
                v-model="identityInfo.city"
                label="City"
                :maxlength="75" 
                is-required
                :field-status="getFieldStatus('city')"
                :message="getFieldMessage('city')"
                data-validation-field="city"
                data-id="identity-city" />

            <div class="state-zip">
                <app-column-group-layout>
                    <app-dropdown
                        v-model="selectedState"
                        open-up
                        :options="US_STATES"
                        id-field="abbreviation"
                        display-field="name"
                        label="State"
                        is-required
                        auto-open
                        @update:modelValue="updateState"
                        :field-status="getFieldStatus('state')"
                        :message="getFieldMessage('state')"
                        data-validation-field="state"
                        data-id="identity-state" />

                    <app-masked-input 
                        v-model="identityInfo.zip"
                        label="Zip code"
                        :maxlength="5" 
                        mask="#####"
                        is-required
                        input-mode="numeric"
                        :field-status="getFieldStatus('zip')"
                        :message="getFieldMessage('zip')"
                        data-validation-field="zip"
                        data-id="identity-zip" />
                </app-column-group-layout>
            </div>

        <action-bar-layout>
            <app-button
                size="large"
                @onClick="verifyIdentity"
                data-id="identity-continue"
                :is-loading="isSavng">
                Continue
            </app-button>
        </action-bar-layout>

        </app-form-content-layout>

    </flow-layout>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.loader {
    margin-top: 5rem;
}

// Duplicated by the mobile nav bar below 1023px, which is FlowLayout's own header switch.
.page-title {
    @include tablet-and-down {
        display: none;
    }
}

.highlight-banner {

    &__text {
        font-size: 0.9rem;
        font-style: normal;
        font-weight: 400;
        line-height: 150%
    }
}

.form-subheader {
    font-size: 1.25rem;
    font-style: normal;
    font-weight: 700;
    line-height: 150%;
}
</style>